import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { fmtDate, naira } from '@/lib/format';
import {
  getFees, getFeesById, getPayStatus, getWallet,
  payInitiate, payPartial, postBasket, postPayWallet, postWalletQuote, putBasket,
  type PayInstruction, type WalletQuote,
} from '@/api/parent';
import { WalletPinAction } from '../wallet/WalletPinAction';
import { Banner, Empty, ErrorBox, Loading, Modal, errMsg, field, unwrap, PageTitle } from '../ui';

// ── Toggle: parent pays via wallet only right now. Flip to false to re-enable bank transfer.
const WALLET_ONLY = true;

interface FeeChild { student_id: number; name: string; admission_no: string; can_pay: boolean; owed: number; results_held: boolean }
interface Invoice { id: number; number: string; total: number; amount_paid: number; balance: number; status: string; due_date: string | null; items: { id: number; description: string; amount: number; discount?: number; is_optional?: boolean; selected?: boolean; paid?: number; line_status?: string; handed_out?: boolean; contents?: { name: string; quantity: number; unit_price: number }[] }[] }
interface FeeDetail { student: { id: number; name: string }; invoices: Invoice[]; recent_payments: { id?: number; reference?: string; amount: number; paid_at?: string | null }[]; pay_to: PayInstruction | null }

function StatusRow({ p }: { p: PayInstruction }) {
  const q = useQuery({
    queryKey: ['parent-pay-status', p.payment_id],
    queryFn: () => unwrap<{ status: string; receipt_no: string | null }>(getPayStatus(p.payment_id)),
    refetchInterval: (query) => (query.state.data && query.state.data.status !== 'pending' ? false : 5000),
  });
  return (
    <div className="border rounded-xl p-3 text-sm space-y-1">
      <div className="flex justify-between"><span className="font-medium">{p.student}</span><span>{naira(p.amount)}</span></div>
      {p.ready === false || !p.account_number ? <p className="text-amber-700">{p.note}</p> : (
        <>
          <div>Transfer exactly <b>{naira((p as any).total_to_send ?? p.amount)}</b> to <b>{p.account_number}</b> — {p.account_name} · {p.bank_name}</div>
          {Number((p as any).charge ?? 0) > 0 && <div className="text-xs text-slate-500">That is {naira(p.amount)} fees + {naira((p as any).charge)} {((p as any).charges ?? []).map((c: any) => c.name).join(', ') || 'charges'}.</div>}
          <div className="text-slate-500">{p.note}</div>
        </>
      )}
      <div className="flex items-center gap-2">Status: <StatusBadge status={q.data?.status ?? 'pending'} />{q.data?.receipt_no && <span className="text-slate-500">Receipt {q.data.receipt_no}</span>}{q.data?.status === 'pending' && <span className="text-slate-400">checking every few seconds…</span>}</div>
    </div>
  );
}

export function FeesPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Record<number, string>>({});
  const [basketId, setBasketId] = useState<number | null>(null);
  const [paying, setPaying] = useState<'wallet' | null>(null);
  const [transfer, setTransfer] = useState<PayInstruction[] | null>(null);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const transferKey = useRef(crypto.randomUUID());
  const [partial, setPartial] = useState<{ invoice: Invoice; amount: string; key: string } | null>(null);

  const fees = useQuery({ queryKey: ['parent-fees'], queryFn: () => unwrap<{ children: FeeChild[]; total_owed: number }>(getFees()) });
  const wallet = useQuery({ queryKey: ['parent-wallet'], queryFn: () => getWallet() });
  const children = fees.data?.children ?? [];
  const studentId = params.get('student') ?? (children[0] ? String(children[0].student_id) : '');
  const child = children.find((c) => String(c.student_id) === studentId);
  const detail = useQuery({ queryKey: ['parent-fees', studentId], queryFn: () => unwrap<FeeDetail>(getFeesById(studentId)), enabled: !!studentId });

  useEffect(() => { setSelected({}); setBasketId(null); }, [studentId]);
  useEffect(() => { transferKey.current = crypto.randomUUID(); }, [selected]);

  const invoices = detail.data?.invoices ?? [];
  const due = invoices.filter((i) => i.balance > 0);
  const lines = due.filter((i) => selected[i.id] !== undefined).map((i) => ({ invoice_id: i.id, amount: Number(selected[i.id]) }));
  const amountsOk = lines.length > 0 && due.filter((i) => selected[i.id] !== undefined).every((i) => Number(selected[i.id]) >= 1 && Number(selected[i.id]) <= i.balance);
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const w = wallet.data;
  const walletReady = !!w && w.onboarded && !w.requires_bvn && w.has_pin;

  // ── Quote (Fees + Charge = Total) — fetched upfront so parents see it before confirming.
  const [quote, setQuote] = useState<WalletQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  useEffect(() => {
    if (total <= 0 || !walletReady) { setQuote(null); return; }
    let live = true;
    setQuoteLoading(true);
    postWalletQuote({ type: 'fee_payment', amount: total })
      .then((q) => { if (live) setQuote(q); })
      .catch(() => { if (live) setQuote(null); })
      .finally(() => { if (live) setQuoteLoading(false); });
    return () => { live = false; };
  }, [total, walletReady]);

  const walletBalance = Number(w?.balance ?? 0);
  const totalWithCharge = quote?.total ?? total;
  const hasEnough = walletReady && !quoteLoading && !!quote && walletBalance >= totalWithCharge;

  const toggle = (i: Invoice) => setSelected((p) => { const n = { ...p }; if (n[i.id] !== undefined) delete n[i.id]; else n[i.id] = String(i.balance); return n; });
  const reload = () => qc.invalidateQueries({ predicate: (q) => ['parent-fees', 'parent-wallet', 'parent-home', 'parent-payments'].some((k) => String(q.queryKey[0]).startsWith(k)) });

  const ensureBasket = async () => {
    const items = lines.map((l) => ({ invoice_id: l.invoice_id, amount: l.amount }));
    const r = basketId ? await putBasket({ basket_id: basketId, items }) : await postBasket({ items });
    const id = (r as { data: { data: { id: number } } }).data.data.id;
    setBasketId(id);
    return id;
  };

  const startWallet = async () => {
    setBusy(true); setMsg(null);
    try { await ensureBasket(); setPaying('wallet'); } catch (e) { setMsg({ tone: 'error', text: errMsg(e) }); } finally { setBusy(false); }
  };

  // Kept for future re-enablement of bank transfer:
  const startTransfer = async () => {
    setBusy(true); setMsg(null);
    try {
      const id = await ensureBasket();
      const r = await payInitiate({ basket_id: id }, transferKey.current);
      setTransfer(r.payments);
      reload();
    } catch (e) { setMsg({ tone: 'error', text: errMsg(e) }); } finally { setBusy(false); }
  };
  const startPartial = async () => {
    if (!partial) return;
    setBusy(true); setMsg(null);
    try {
      const r = await payPartial({ invoice_id: partial.invoice.id, amount: Number(partial.amount) }, partial.key);
      setPartial(null);
      setTransfer(r.payments);
      reload();
    } catch (e) { setMsg({ tone: 'error', text: errMsg(e) }); } finally { setBusy(false); }
  };

  const remaining = (i: Invoice) => i.balance;
  return (
    <div className="space-y-4 max-w-4xl pb-28">
      <PageTitle kicker="Payments" title="Pay school fees" sub="Tick what you want to pay. You can change the amount to pay part of an invoice. Payment comes from your wallet and needs your PIN." />
      {msg && <Banner tone={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Banner>}
      {fees.isLoading ? <Loading /> : fees.isError ? <ErrorBox error={fees.error} onRetry={() => fees.refetch()} /> : !children.length ? <Card><Empty text="No children linked yet." /></Card> : (
        <>
          {children.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {children.map((c) => (
                <button key={c.student_id} onClick={() => setParams({ student: String(c.student_id) })}
                  className={`shrink-0 text-left rounded-2xl border px-3 py-2 ${String(c.student_id) === studentId ? 'border-[#0A2540] bg-[#0A2540] text-white' : 'border-slate-200 bg-white'}`}>
                  <span className="block text-sm font-semibold">{c.name}</span>
                  <span className={`block text-xs ${String(c.student_id) === studentId ? 'text-white/70' : c.owed > 0 ? 'text-red-600' : 'text-green-700'}`}>{c.owed > 0 ? `${naira(c.owed)} owed` : 'All paid'}</span>
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-3.5"><p className="text-xs text-slate-500">{child?.name ?? 'This child'} owes</p><p className={`text-xl font-extrabold ${child && child.owed > 0 ? 'text-red-600' : 'text-green-700'}`}>{naira(child?.owed ?? 0)}</p></div>
            <Link to="/parent/wallet" className="bg-white rounded-2xl border border-slate-200 p-3.5 block"><p className="text-xs text-slate-500">Wallet balance</p><p className="text-xl font-extrabold text-slate-900">{w && w.onboarded ? naira(walletBalance) : 'Not opened'}</p></Link>
          </div>

          {!walletReady && (
            <div className="rounded-2xl p-4 border bg-blue-50 border-blue-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div><p className="text-sm font-semibold text-blue-900">{w && w.onboarded && !w.has_pin ? 'Set your transaction PIN to pay' : 'Open your wallet to pay fees online'}</p>
                <p className="text-xs text-blue-700 mt-0.5">{w && w.onboarded && !w.has_pin ? 'It takes a few seconds.' : 'It takes about a minute. You get your own account number to top up from any bank.'}</p></div>
              <Link to="/parent/wallet" className="inline-flex items-center justify-center px-4 py-2.5 bg-[#0A2540] text-white text-sm font-bold rounded-xl whitespace-nowrap">{w && w.onboarded && !w.has_pin ? 'Set PIN →' : 'Open wallet →'}</Link>
            </div>
          )}

          <section className="bg-white rounded-2xl border border-slate-200">
            <h2 className="font-bold px-4 pt-3.5 pb-2">Invoices</h2>
            {detail.isLoading ? <p className="px-4 pb-4"><Loading /></p> : detail.isError ? <div className="px-4 pb-4"><ErrorBox error={detail.error} onRetry={() => detail.refetch()} /></div> : !invoices.length ? <p className="px-4 pb-4 text-sm text-slate-500">No invoices for this child.</p> : (
              <ul className="divide-y divide-slate-100">
                {invoices.map((i) => {
                  const on = selected[i.id] !== undefined;
                  const payable = i.balance > 0 && child?.can_pay !== false;
                  return (
                    <li key={i.id} className={`px-4 py-3 ${on ? 'bg-green-50/60' : ''}`}>
                      <label className={`flex items-start gap-3 ${payable ? 'cursor-pointer' : ''}`}>
                        <input type="checkbox" disabled={!payable} checked={on} onChange={() => toggle(i)} className="mt-1 w-5 h-5 accent-[#0A2540]" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-slate-900 text-sm">{i.items.map((x) => x.description).join(', ') || i.number}</p>
                            <p className={`text-sm font-bold shrink-0 ${i.balance > 0 ? 'text-slate-900' : 'text-green-700'}`}>{i.balance > 0 ? naira(remaining(i)) : 'Paid'}</p>
                          </div>
                          <p className="text-xs text-slate-500">{i.number}{i.due_date ? ` · due ${fmtDate(i.due_date)}` : ''}{i.amount_paid > 0 ? ` · ${naira(i.amount_paid)} paid of ${naira(i.total)}` : ''}</p>
                        </div>
                      </label>
                      <details className="mt-1.5 ml-8 group">
                        <summary className="text-xs font-semibold text-[#0A2540] cursor-pointer list-none">What's on this invoice ▾</summary>
                        <ul className="mt-1.5 rounded-xl bg-slate-50 divide-y divide-slate-200/70">
                          {i.items.filter((x) => x.selected !== false).map((x) => (
                            <li key={x.id} className="px-3 py-2 text-xs">
                              <div className="flex justify-between gap-2"><span className="font-semibold text-slate-800">{x.description}{x.is_optional ? <span className="ml-1 text-[10px] text-blue-700">(optional)</span> : null}</span>
                                <span className="shrink-0">{naira(Number(x.amount) - Number(x.discount ?? 0))} <span className={x.line_status === 'paid' ? 'text-green-700 font-semibold' : x.line_status === 'part' ? 'text-amber-700' : 'text-slate-400'}>{x.line_status === 'paid' ? '✓ paid' : x.line_status === 'part' ? `· ${naira(x.paid ?? 0)} paid` : '· not paid'}</span></span></div>
                              {(x.contents ?? []).length > 0 && <p className="text-slate-500 mt-0.5">{x.contents!.map((c) => `${c.quantity} × ${c.name}`).join(', ')}{x.handed_out ? ' · collected ✓' : x.line_status === 'paid' ? ' · ready to collect' : ' · collect from school once paid'}</p>}
                            </li>
                          ))}
                        </ul>
                      </details>
                      {on && (
                        <div className="mt-2 ml-8 flex items-center gap-2">
                          <span className="text-xs text-slate-500">Pay</span>
                          <div className="relative w-40"><span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₦</span>
                            <input className={`${field} !pl-6 !py-1.5 text-right`} inputMode="decimal" value={selected[i.id]} onChange={(e) => setSelected((p) => ({ ...p, [i.id]: e.target.value.replace(/[^\d.]/g, '') }))} /></div>
                          {Number(selected[i.id]) !== i.balance && <button className="text-xs font-semibold text-[#0A2540]" onClick={() => setSelected((p) => ({ ...p, [i.id]: String(i.balance) }))}>Full</button>}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {child?.can_pay === false && <p className="text-sm text-amber-700 px-4 pb-3">You don't have permission to pay for this child.</p>}
          </section>

          {!!detail.data?.recent_payments?.length && (
            <section className="bg-white rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between px-4 pt-3.5 pb-2"><h2 className="font-bold">Recent payments</h2><Link to="/parent/payment-history" className="text-xs font-semibold text-[#0A2540]">All & receipts →</Link></div>
              <ul className="divide-y divide-slate-100">
                {detail.data.recent_payments.map((p, idx) => <li key={p.id ?? idx} className="px-4 py-2.5 flex justify-between text-sm"><span className="text-slate-600">{p.paid_at ? fmtDate(p.paid_at) : ''} <span className="text-xs text-slate-400">{p.reference ?? ''}</span></span><span className="font-semibold">{naira(p.amount)}</span></li>)}
              </ul>
            </section>
          )}

          {!WALLET_ONLY && detail.data?.pay_to?.ready && detail.data.pay_to.account_number && (
            <Card title="Pay to this child's account">
              <p className="text-sm"><b>{detail.data.pay_to.account_number}</b> — {detail.data.pay_to.account_name} · {detail.data.pay_to.bank_name}</p>
              <p className="text-xs text-slate-500 mt-1">{detail.data.pay_to.note}</p>
            </Card>
          )}

          {lines.length > 0 && (
            <div className="fixed bottom-0 inset-x-0 md:left-64 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3">
              <div className="max-w-4xl flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500">{lines.length} selected{quote && quote.total > total ? ` · incl. ${naira(quote.total - total)} charges` : ''}</p>
                  <p className="text-lg font-extrabold">{quoteLoading ? '…' : naira(quote?.total ?? total)}</p>
                  {!amountsOk && <p className="text-xs text-red-600">Each amount must be between ₦1 and the balance.</p>}
                  {walletReady && quote && !hasEnough && <p className="text-xs text-red-600">You need {naira(totalWithCharge - walletBalance)} more. <Link to="/parent/wallet" className="underline font-semibold">Top up</Link></p>}
                </div>
                <button disabled={!walletReady || !amountsOk || busy || !quote || !hasEnough} onClick={startWallet}
                  className="px-5 py-3 rounded-xl bg-[#0A2540] text-white text-sm font-bold disabled:opacity-40 shrink-0">
                  {!walletReady ? 'Wallet needed' : busy ? 'Preparing…' : 'Pay now'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {paying === 'wallet' && basketId && (
        <WalletPinAction
          title="Pay school fees"
          quoteType="fee_payment"
          amount={total}
          amountLabel="School fees"
          balance={walletBalance}
          details={[{ label: 'Child', value: child?.name ?? '' }, { label: 'Invoices', value: String(lines.length) }]}
          onCancel={() => setPaying(null)}
          onFinished={() => { setSelected({}); setBasketId(null); reload(); }}
          onNeedWallet={() => { setPaying(null); reload(); }}
          onNeedPin={() => { setPaying(null); reload(); }}
          run={async (pin, key) => {
            const r = await postPayWallet({ basket_id: basketId }, pin, key);
            return `Paid ${naira(r.total)} for ${child?.name ?? 'your child'}. Your receipt is in Payments & receipts. New wallet balance: ${naira(r.wallet_balance)}.`;
          }}
        />
      )}

      {partial && (
        <Modal title={`Pay part of ${partial.invoice.number}`} onClose={() => setPartial(null)}>
          <div className="space-y-3 text-sm">
            <p>Balance: <b>{naira(partial.invoice.balance)}</b>. Enter how much you want to pay now (at least ₦100). You will get an account to transfer to.</p>
            <input className={field} inputMode="decimal" autoFocus placeholder="Amount" value={partial.amount} onChange={(e) => setPartial({ ...partial, amount: e.target.value.replace(/[^\d.]/g, '') })} />
            {partial.amount !== '' && (Number(partial.amount) < 100 || Number(partial.amount) > partial.invoice.balance) && <p className="text-red-600">Amount must be between ₦100 and the balance.</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPartial(null)}>Cancel</Button>
              <Button disabled={busy || !(Number(partial.amount) >= 100 && Number(partial.amount) <= partial.invoice.balance)} onClick={startPartial}>{busy ? 'Preparing…' : 'Get transfer details'}</Button>
            </div>
          </div>
        </Modal>
      )}

      {transfer && (
        <Modal title="Pay by bank transfer" onClose={() => { setTransfer(null); setSelected({}); setBasketId(null); reload(); }}>
          <div className="space-y-3">
            {transfer.map((p) => <StatusRow key={p.payment_id} p={p} />)}
            <p className="text-xs text-slate-500">Keep this window open or check Payment History — payment is confirmed automatically once your transfer arrives.</p>
          </div>
        </Modal>
      )}
    </div>
  );
}
